import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { translations } from "../i18n/translations";
import { resolveImage } from "../lib/image";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentContext = createContext(null);

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

// Deep merge: server overrides defaults; arrays from server replace arrays in defaults.
const deepMerge = (defaults, override) => {
  if (override === undefined || override === null) return defaults;
  if (!isPlainObject(defaults) || !isPlainObject(override)) return override;
  const out = { ...defaults };
  for (const k of Object.keys(override)) {
    out[k] = deepMerge(defaults[k], override[k]);
  }
  return out;
};

const buildDefaults = () => ({
  ru: translations.ru,
  en: translations.en,
  branding: {
    company_name: "DIYATOUR",
    tagline: "CHINA · BUSINESS · TECH",
    logo_url:
      "https://customer-assets.emergentagent.com/job_romantic-kare-8/artifacts/vp7vdfut_wdaw2.png",
  },
  channels: {
    telegram_url: "https://t.me/diyatour",
    telegram_handle: "@diyatour",
    whatsapp_url: "https://wa.me/0000000000",
    whatsapp_handle: "+86 000 000 0000",
    wechat_handle: "diyatour",
    email: "hello@diyatour.com",
  },
  visibility: {
    home_hero: true,
    home_popular: true,
    home_inspire: true,
    home_benchmark: true,
    home_why: true,
    home_map: true,
    home_cta: true,
  },
});

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(() => buildDefaults());

  const refresh = async () => {
    try {
      const res = await axios.get(`${API_BASE}/content`);
      setContent(deepMerge(buildDefaults(), res.data));
    } catch (e) {
      // keep defaults
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const logoUrl = resolveImage(content?.branding?.logo_url);
    if (!logoUrl) return;

    const upsertLink = (rel, href) => {
      let el = document.querySelector(`link[rel='${rel}']`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    upsertLink("icon", logoUrl);
    upsertLink("shortcut icon", logoUrl);
    upsertLink("apple-touch-icon", logoUrl);
  }, [content?.branding?.logo_url]);

  return (
    <ContentContext.Provider value={{ content, refresh, setContent }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
};
